import { useEffect, useState } from "react";
import { ShieldCheck, Laptop } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { MfaSetup, BackupCodes } from "@/components/MfaStep";
import { api, formatError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CodeAction = ({ label, testId, onRun }) => {
  const [code, setCode] = useState("");
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Input data-testid={`${testId}-code`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="h-9 w-36 font-mono" />
      <button data-testid={`${testId}-btn`} disabled={code.length !== 6} onClick={() => onRun(code).then(() => setCode(""))} className="h-9 px-4 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">{label}</button>
    </div>
  );
};

const TrustedDevices = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/auth/mfa/devices").then((r) => setRows(r.data)); }, []);
  const revoke = async (id) => {
    try {
      await api.delete(`/auth/mfa/devices/${id}`);
      setRows(rows.filter((d) => d.id !== id));
      toast.success("Device removed");
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  if (!rows.length) return null;
  return (
    <div data-testid="trusted-devices" className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Trusted devices</p>
      <ul className="space-y-2">{rows.map((d) => (
        <li key={d.id} data-testid={`trusted-device-${d.id}`} className="flex flex-wrap items-center gap-3 text-sm">
          <Laptop size={15} className="text-slate-400" /><span className="flex-1">{d.label} <span className="text-xs text-slate-500">· last used {fmtDate(d.last_used_at)} · until {fmtDate(d.expires_at)}</span></span>
          <button data-testid={`trusted-device-remove-${d.id}`} onClick={() => revoke(d.id)} className="text-xs font-semibold text-red-600 underline">Remove</button>
        </li>))}</ul>
    </div>
  );
};

const SignInHistory = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/auth/sign-ins").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows?.length) return null;
  return (
    <div data-testid="sign-in-history" className="mt-6">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Recent sign-ins</p>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">{rows.map((s, i) => (
        <li key={s.at + i} data-testid={`sign-in-row-${i}`} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-mono text-xs text-slate-500 w-36">{fmtDate(s.at)}</span>
          <span className="flex-1 min-w-[160px]">{s.device} <span className="text-xs text-slate-500">· network {s.network}</span></span>
          {(s.new_device || s.new_location) && <span className="text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">{s.new_device ? "New device" : "New location"}</span>}
          <span className="text-[10px] uppercase tracking-wider text-slate-400">{s.method.replace("_", " ")}</span>
        </li>))}</ul>
    </div>
  );
};

export const SecurityPanel = () => {
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(false);
  const [codes, setCodes] = useState(null);
  const refresh = async () => setUser((await api.get("/auth/me")).data);
  const call = async (path, code, msg) => {
    try {
      const { data } = await api.post(path, { code });
      if (data.backup_codes) setCodes(data.backup_codes);
      toast.success(msg);
      await refresh();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return (
    <Panel title="Two-step sign-in" testId="security-panel">
      {user && !user.mfa_available ? <p className="text-sm text-slate-500" data-testid="mfa-paused">Two-step sign-in is switched off for now. Sign-in uses email and password only.</p> : <>
      <p className="text-sm flex items-center gap-2" data-testid="mfa-status"><ShieldCheck size={16} className={user?.mfa_enabled ? "text-emerald-600" : "text-slate-400"} />
        {user?.mfa_enabled ? "On: you'll enter a code from your authenticator app each time you sign in." : "Off: add an authenticator app code to every sign-in for extra protection."}</p>
      {user?.mfa_required && <p className="text-xs text-slate-500 mt-1">Required for your role.</p>}
      <div className="mt-4 space-y-3">
        {codes && <BackupCodes codes={codes} onContinue={() => setCodes(null)} />}
        {!user?.mfa_enabled && !setup && <button data-testid="mfa-enable-btn" onClick={() => setSetup(true)} className="h-10 px-5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500">Set up two-step sign-in</button>}
        {!user?.mfa_enabled && setup && <MfaSetup onDone={(r) => { setSetup(false); setCodes(r.backup_codes); refresh(); }} />}
        {user?.mfa_enabled && !codes && <CodeAction label="New backup codes" testId="mfa-regen" onRun={(c) => call("/auth/mfa/backup-codes", c, "New backup codes created")} />}
        {user?.mfa_enabled && !user?.mfa_required && !codes && <CodeAction label="Turn off" testId="mfa-disable" onRun={(c) => call("/auth/mfa/disable", c, "Two-step sign-in turned off")} />}
      </div>
      {user?.mfa_enabled && <TrustedDevices />}
      </>}
      {user?.mfa_required && <p className="text-xs text-slate-500 mt-4" data-testid="login-alerts-note">We email you whenever your account signs in from a new device or location.</p>}
      {user?.mfa_required && <SignInHistory />}
    </Panel>
  );
};

export const MfaResetCell = ({ u, onDone }) => {
  if (!u.mfa_enabled) return <span className="text-xs text-slate-400">Off</span>;
  const reset = async () => {
    const reason = window.prompt(`Reset two-step sign-in for ${u.email}? Only do this after checking who they are. Enter the reason (recorded in the audit trail):`);
    if (!reason) return;
    try {
      await api.post(`/admin/users/${u.id}/mfa-reset`, { reason, identity_checked: true });
      toast.success("Two-step sign-in reset");
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  return <button data-testid={`admin-mfa-reset-${u.id}`} onClick={reset} className="text-xs font-semibold text-red-600 underline">On · Reset</button>;
};
